// Runtime configuration utility
declare global {
  interface Window {
    ENV: {
      VITE_API_URL: string;
      VITE_KEYCLOAK_URL: string;
      VITE_KEYCLOAK_REALM: string;
      VITE_KEYCLOAK_CLIENT_ID: string;
      VITE_ACCOUNTS_API_URL: string;
      VITE_ACCOUNTS_CLIENT_ID: string;
      VITE_EMBED_PARENT_ORIGIN: string;
      VITE_ENVIRONMENT: string;
      VITE_APP_VERSION: string;
      VITE_FARO_ENABLED: string;
      VITE_FARO_COLLECTOR_URL: string;
      VITE_FARO_APP_NAME: string;
      VITE_FARO_API_KEY: string;
    };
  }
}

// Helper function to get configuration values
// Tries runtime config first, falls back to build-time env vars
function getConfig(key: keyof Window['ENV']): string {
  // Ensure we're in a browser environment
  if (typeof window === 'undefined') {
    // Fall back to build-time environment variables in SSR or other non-browser contexts
    return getBuildTimeConfig(key);
  }

  // Try runtime config from window.ENV
  if (window.ENV && window.ENV[key] && !window.ENV[key].startsWith('__')) {
    return window.ENV[key];
  }

  // Fall back to build-time environment variables
  return getBuildTimeConfig(key);
}

function getBuildTimeConfig(key: keyof Window['ENV']): string {
  switch (key) {
    case 'VITE_API_URL':
      return import.meta.env.VITE_API_URL || 'https://api.extract.hazcheck.com';
    case 'VITE_KEYCLOAK_URL':
      return import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.hazcheck.com/auth';
    case 'VITE_KEYCLOAK_REALM':
      return import.meta.env.VITE_KEYCLOAK_REALM || 'Hazcheck';
    case 'VITE_KEYCLOAK_CLIENT_ID':
      return import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'document-extraction';
    case 'VITE_ACCOUNTS_API_URL':
      return import.meta.env.VITE_ACCOUNTS_API_URL || 'https://api.accounts.validate.hazcheck.com';
    case 'VITE_ACCOUNTS_CLIENT_ID':
      return import.meta.env.VITE_ACCOUNTS_CLIENT_ID || 'document-extraction-backend';
    case 'VITE_EMBED_PARENT_ORIGIN':
      return import.meta.env.VITE_EMBED_PARENT_ORIGIN || 'https://validate.hazcheck.com';
    case 'VITE_ENVIRONMENT':
      return import.meta.env.VITE_ENVIRONMENT || 'local';
    case 'VITE_APP_VERSION':
      return import.meta.env.VITE_APP_VERSION || '';
    case 'VITE_FARO_ENABLED':
      return import.meta.env.VITE_FARO_ENABLED || 'false';
    case 'VITE_FARO_COLLECTOR_URL':
      return import.meta.env.VITE_FARO_COLLECTOR_URL || 'https://faro-extract.hazcheck.com/collect';
    case 'VITE_FARO_APP_NAME':
      return import.meta.env.VITE_FARO_APP_NAME || 'extract-ui';
    case 'VITE_FARO_API_KEY':
      return import.meta.env.VITE_FARO_API_KEY || '';
    default:
      return '';
  }
}

export const config = {
  get API_URL() {
    return getConfig('VITE_API_URL');
  },
  get KEYCLOAK_URL() {
    return getConfig('VITE_KEYCLOAK_URL');
  },
  get KEYCLOAK_REALM() {
    return getConfig('VITE_KEYCLOAK_REALM');
  },
  get KEYCLOAK_CLIENT_ID() {
    return getConfig('VITE_KEYCLOAK_CLIENT_ID');
  },
  get ACCOUNTS_API_URL() {
    return getConfig('VITE_ACCOUNTS_API_URL');
  },
  get ACCOUNTS_CLIENT_ID() {
    return getConfig('VITE_ACCOUNTS_CLIENT_ID');
  },
  // Comma-separated allow-list of parent origins permitted to embed Extract.
  get EMBED_PARENT_ORIGIN() {
    return getConfig('VITE_EMBED_PARENT_ORIGIN');
  },
  get ENVIRONMENT() {
    return getConfig('VITE_ENVIRONMENT');
  },
  get APP_VERSION() {
    return getConfig('VITE_APP_VERSION');
  },
  get FARO_ENABLED() {
    return getConfig('VITE_FARO_ENABLED') === 'true';
  },
  get FARO_COLLECTOR_URL() {
    return getConfig('VITE_FARO_COLLECTOR_URL');
  },
  get FARO_APP_NAME() {
    return getConfig('VITE_FARO_APP_NAME');
  },
  get FARO_API_KEY() {
    return getConfig('VITE_FARO_API_KEY');
  },
};
